import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { getContrastTextColor } from "src/utils/color";
import { valueToLabel } from "../common";
import { SearchContext } from "src/search/useSearchContext";
import styles from "./Tooltip.module.css";
import { CustomTooltip } from "src/components/CustomTooltip";
import { TooltipContentModule, TooltipContext } from "./tooltip-types";

interface TreeMapNode {
  id: string;
  depth: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  data: {
    name: string;
    value: number;
    total?: number;
    properties?: any[];
    values?: any[];
    measureValue?: { label: string; value: string };
    ancestors?: { name: string; attribute: string }[];
  };
  parent?: TreeMapNode;
  children?: TreeMapNode[];
}

interface TreeMapCellProps {
  node: TreeMapNode;
  context: SearchContext;
  activeValue: string;
  legendPin: string | undefined;
  token: any;
  popup: any;
  labels: boolean;
  colorMap: Map<string, string>;
  defaultColor: string;
}

interface TreeMapCellTooltipProps {
  node: TreeMapNode;
  context: SearchContext;
  total: number;
  min: number;
  max: number;
  children: React.ReactNode;
}

// Tooltip content module following the pattern of other tooltips
export const TreeMapTooltipModule: TooltipContentModule = {
  type: "group",
  priority: 1,
  shouldRender: (context: TooltipContext) => {
    return context.type === "group";
  },
  render: (context: TooltipContext) => {
    if (!context.record) return null;

    const aggregation = context.aggregations?.[0];
    const { min, max, total, positionInRange } = context.metadata;
    const percentageOfTotal =
      total > 0 ? (context.record.value / total) * 100 : 0;

    return (
      <div className={styles.tooltip_container}>
        <div className={styles.tooltip_header}>Cell Information</div>
        <div>
          <div className={styles.tooltip_section}>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Property</div>
              <span className={styles.tooltip_value}>
                {context.record.name}
              </span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Field</div>
              <span className={styles.tooltip_value}>
                {context.record.attribute}
              </span>
            </div>
            {aggregation && (
              <>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Measure</div>
                  <span className={styles.tooltip_value}>
                    {aggregation.field}
                  </span>
                </div>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Operator</div>
                  <span className={styles.tooltip_value}>
                    {aggregation.reducer}
                  </span>
                </div>
              </>
            )}
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Node Value</div>
              <span className={styles.tooltip_value}>
                {context.record.label}
              </span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>% of Total</div>
              <span className={styles.tooltip_value}>
                {percentageOfTotal.toFixed(1)}%
              </span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Percentile Position</div>
              <span className={styles.tooltip_value}>
                {positionInRange.toFixed(1)}%
              </span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Value Range</div>
              <span className={styles.tooltip_value}>
                {min.toLocaleString()} - {max.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export function TreeMapCellTooltip({
  node,
  context,
  total,
  min,
  max,
  children,
}: TreeMapCellTooltipProps) {
  const { params } = context;
  const groupBys = params.state.groupBys || [];
  const aggregation = params.state.aggregations?.[0];

  const range = max - min;
  const positionInRange =
    range > 0 ? ((node.data.value - min) / range) * 100 : 0;

  const tooltipContext: TooltipContext = {
    type: "group",
    value: node.data.value,
    formattedValue:
      node.data.measureValue?.label || node.data.value?.toString(),
    record: node.data,
    currentColumn: aggregation,
    metadata: {
      level: node.depth,
      path: node.data.ancestors?.map((a) => a.name) || [],
      isLeaf: !node.children?.length,
      depth: node.depth,
      total,
      min,
      max,
      range,
      positionInRange,
    },
    aggregations: [aggregation].filter(Boolean),
    groupBys,
    children: null,
  };

  return (
    <CustomTooltip
      content={TreeMapTooltipModule.render(tooltipContext)}
      placement="right"
      size="medium"
      overlayStyle={{ maxWidth: 400 }}
    >
      {children}
    </CustomTooltip>
  );
}
